import { useEffect, useRef, useCallback } from 'react';
import { messageAPI } from '../lib/api';
import { toast } from 'sonner';

const POLL_INTERVAL = 60000; // 1 minute
const SEEN_ALERTS_KEY = 'messagestore_seen_alerts';

// Get seen alert IDs from localStorage
const getSeenAlerts = () => {
  try {
    const stored = localStorage.getItem(SEEN_ALERTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save seen alert IDs to localStorage
const saveSeenAlerts = (alertIds) => {
  try {
    // Keep only last 100 alerts to prevent localStorage bloat
    const trimmed = alertIds.slice(-100);
    localStorage.setItem(SEEN_ALERTS_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
};

export function useAlertNotifications(enabled = true) {
  const seenAlertsRef = useRef(getSeenAlerts());
  const isFirstLoadRef = useRef(true);

  const checkForNewAlerts = useCallback(async () => {
    if (!enabled) return;

    try {
      // Fetch only unread alerts
      const response = await messageAPI.getMessages({
        type: 'alert',
        status: 'unread',
        size: 20
      });

      const alerts = response.data.messages || [];
      const seenAlerts = seenAlertsRef.current;

      // Find new alerts that haven't been seen
      const newAlerts = alerts.filter(
        alert => !seenAlerts.includes(alert.message_id)
      );

      // On first load, just mark all current alerts as seen without notifying
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        const allAlertIds = [...seenAlerts, ...alerts.map(a => a.message_id)];
        seenAlertsRef.current = [...new Set(allAlertIds)];
        saveSeenAlerts(seenAlertsRef.current);
        return;
      }

      // Show toast for each new alert
      newAlerts.forEach(alert => {
        toast.warning(alert.subject, {
          description: `New alert from ${alert.source}`,
          duration: 8000,
          action: {
            label: 'View',
            onClick: () => {
              window.location.href = `/messages/${alert.message_id}`;
            }
          }
        });

        // Mark as seen
        seenAlertsRef.current.push(alert.message_id);
      });

      // Save updated seen alerts
      if (newAlerts.length > 0) {
        saveSeenAlerts(seenAlertsRef.current);
      }

    } catch (error) {
      // Silently fail - don't show errors for background polling
      console.debug('Alert polling failed:', error);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Initial check after a short delay
    const initialTimeout = setTimeout(checkForNewAlerts, 2000);

    // Set up polling interval
    const intervalId = setInterval(checkForNewAlerts, POLL_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [enabled, checkForNewAlerts]);

  // Return function to manually trigger check
  return { checkForNewAlerts };
}
